import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import {
  campaignDetailsResolveConflict,
  campaignDetailsSave,
} from '@disco/state-management/actions/campaign-details-actions'
import { campaignListBeginEdit } from '@disco/state-management/actions/campaign-list-actions'
import {
  getCampaign,
  getCampaignEditorState,
} from '@disco/state-management/selectors/campaigns'
import dayjs from '@disco/utils/dayjs'

import type { EmailCampaign } from 'types/campaign'
import type { State } from 'types/redux'

import ModalDialog from 'components/ui/modal-dialog'
import Spinner from 'components/ui/spinner'

import type { RootState } from 'store/configureStore'

interface OwnProps {
  params: {
    campaignId?: string
  }
}

function mapStateToProps(state: RootState, ownProps: OwnProps) {
  return {
    campaign: getCampaign(state, {
      campaignId: parseInt(ownProps.params.campaignId),
    }),
    campaignTemplate: getCampaignEditorState(state).sections,
    editingCampaignId: getCampaignEditorState(state).id,
  }
}

const mapDispatchToProps = {
  campaignListBeginEdit,
  campaignDetailsSave,
  campaignDetailsResolveConflict,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>
interface AdditionalProps {
  children: React.ReactNode
  router: any
  route: any
}

type Props = PropsFromRedux & OwnProps & AdditionalProps

class CampaignMasterPage extends React.Component<
  Props,
  {
    quitModal:
      | {
          destinationRoute: Record<string, any> // Object from react-router
        }
      | null
      | undefined
  }
> {
  state = {
    quitModal: null,
  }
  saved = false

  // TODO: use useBlocker in v7 instead of router.setRouteLeaveHook
  // https://reactrouter.com/en/main/hooks/use-blocker
  // doesn't work in v6 with BrowserRouter https://reactrouter.com/6.30.1/hooks/use-blocker

  /*
  _routerWillLeave = (nextRoute: Location) => {
    // true in setRouteLeaveHook = leave
    if (!this.props.campaign) return true
    // we can't do anything if session is expired
    if (
      nextRoute.pathname === '/logout' &&
      nextRoute.query.sessionExpired === '1'
    )
      return true
    if (this.props.campaign.sending || this.props.campaign.status === 'sent')
      return true
    if (!this.props.campaign.dirty) return true
    if (this.saved) return true
    this.setState({
      quitModal: {
        destinationRoute: nextRoute,
      },
    })
    return false // false in setRouteLeaveHook = stay
  }
  */

  componentDidMount() {
    if (this.props.params.campaignId)
      this.props.campaignListBeginEdit(parseInt(this.props.params.campaignId))

    ///this.props.router.setRouteLeaveHook(this.props.route, this._routerWillLeave)
  }

  componentDidUpdate(prevProps: Props) {
    const { campaignId } = this.props.params
    if (!campaignId) return

    const routeChanged = campaignId !== prevProps.params.campaignId
    // a concurrent begin-edit for another campaign can land last and wedge the gate
    const takenByAnotherCampaign =
      this.props.editingCampaignId !== prevProps.editingCampaignId &&
      this.props.editingCampaignId !== Number(campaignId)

    if (routeChanged || takenByAnotherCampaign) {
      this.props.campaignListBeginEdit(parseInt(campaignId))
    }
  }

  _handleSave = async () => {
    const { quitModal } = this.state
    if (!quitModal) return
    const { campaign } = this.props
    if (campaign.status !== 'sent')
      await this.props.campaignDetailsSave(campaign.id, false)
    this.saved = true
    this.props.router.transitionTo(quitModal.destinationRoute)
  }
  _handleReloadNewest = () => {
    this.props.campaignDetailsResolveConflict(
      this.props.campaign.id,
      'CONFLICT_LOAD_LATEST',
    )
  }
  _handleCancelSaving = () => {
    this.props.campaignDetailsResolveConflict(
      this.props.campaign.id,
      'CONFLICT_CANCEL_SAVING',
    )
  }
  _handleCancel = () => {
    this.setState({
      quitModal: null,
    })
  }

  render() {
    const { campaign } = this.props
    // editor slices are global, so wait for this campaign's begin-edit to land
    const isEditingThisCampaign =
      this.props.editingCampaignId === Number(this.props.params.campaignId)
    if (!campaign || !campaign.recipients_list || !isEditingThisCampaign)
      return <Spinner />
    return (
      <React.Fragment>
        {campaign && campaign.conflicting && (
          <ModalDialog
            title="Conflict"
            primaryAction={{
              caption: 'Load latest version',
              onClick: this._handleReloadNewest,
            }}
            status="warning"
            onCancel={this._handleCancelSaving}
          >
            A newer version of this campaign was saved{' '}
            <span
              style={{
                whiteSpace: 'nowrap',
              }}
            >
              {dayjs(campaign.conflicting.newerSavedAt).format('MMM D, h:mma')}
            </span>
            . Would you like to load this latest version?
          </ModalDialog>
        )}
        {this.state.quitModal && (
          <ModalDialog
            title="Save campaign"
            primaryAction={{
              caption: 'Save and close',
              progressCaption: 'Saving...',
              progressIndicator: campaign.saving,
              onClick: this._handleSave,
            }}
            onCancel={this._handleCancel}
          >
            Do you want to save your campaign and continue work later?
          </ModalDialog>
        )}
        <Outlet />
      </React.Fragment>
    )
  }
}

const ConnectedCampaignMasterPage = connector(CampaignMasterPage)

// Wrapper component to provide router props to the class component
const CampaignMasterPageWithRouter = (props: any) => {
  const params = useParams<{ campaignId: string }>()
  const navigate = useNavigate()

  // Create a router object that mimics the old react-router v3/v4 API
  const router = {
    push: navigate,
    replace: (path: string) => navigate(path, { replace: true }),
    setRouteLeaveHook: () => {}, // Simplified for now
    transitionTo: navigate,
  }

  // Create a route object (simplified)
  const route = {}

  return (
    <ConnectedCampaignMasterPage
      {...props}
      params={params}
      router={router}
      route={route}
    />
  )
}

export default CampaignMasterPageWithRouter
